import React from 'react'
import { useTranslation } from 'react-i18next'
import { FaEnvelope } from 'react-icons/fa'
import { Button } from '@components'
import { PermissionSet, useBusinessContext, useFeature, usePermission } from '@context'
import { IInvoice } from '@root/query'
import { postDueAutomationOffState, shouldShowPostDueAutomationDetailAlert } from './postDueUi'
import {
  PostDueAutomationAlertAction,
  PostDueAutomationAlertDescriptionStack,
  PostDueAutomationAlertLayout,
  PostDueAutomationAlertLineStack
} from './PostDueAutomationAlertLayout'

interface Props {
  invoice: IInvoice
  onOpenPostDueSettings: () => void
}

export const PostDueAutomationOffAlert: React.FC<Props> = ({ invoice, onOpenPostDueSettings }) => {
  const { data: business } = useBusinessContext()
  const { enabled: kraviaEnabled } = useFeature('kravia')
  const [t] = useTranslation()
  const hasEditPermission = usePermission(PermissionSet.INVOICING_EDIT)

  const { reminderOff, collectionOff } = postDueAutomationOffState(invoice, business, kraviaEnabled)

  if (!hasEditPermission) {
    return null
  }
  if (!shouldShowPostDueAutomationDetailAlert(invoice, business)) {
    return null
  }
  if (!reminderOff && !collectionOff) {
    return null
  }

  const lines: string[] = []
  if (reminderOff) {
    lines.push(t('invoicing.details.postDueAutomationOff.reminderText'))
  }
  if (collectionOff) {
    lines.push(t('invoicing.details.postDueAutomationOff.collectionText'))
  }

  const alertTitle =
    reminderOff && collectionOff
      ? t('invoicing.details.postDueAutomationOff.titleBoth')
      : reminderOff
      ? t('invoicing.details.postDueAutomationOff.titleReminderOnly')
      : t('invoicing.details.postDueAutomationOff.titleCollectionOnly')

  return (
    <PostDueAutomationAlertLayout
      type="info"
      title={alertTitle}
      description={
        <PostDueAutomationAlertDescriptionStack>
          <PostDueAutomationAlertLineStack>
            {lines.map(line => (
              <div key={line}>{line}</div>
            ))}
          </PostDueAutomationAlertLineStack>
          <PostDueAutomationAlertAction>
            <Button size="sm" icon={<FaEnvelope />} onClick={onOpenPostDueSettings}>
              {t('invoicing.details.postDueAutomationOff.changeSettings')}
            </Button>
          </PostDueAutomationAlertAction>
        </PostDueAutomationAlertDescriptionStack>
      }
    />
  )
}
